import * as THREE from "three";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { Site } from "../models/Site";
import { deleteBlob, saveRecord } from "../api/connection";
import { toPoint } from "mgrs";
import { transferToBlob } from "./transferToBlob";
import { chunkProcess } from "./ioUtils";
import React from "react";
import { Vec3 } from "../models/SyntheticMap";

// export async function fetchSessionToken(apiKey: string): Promise<string> {
//   const url = `https://tile.googleapis.com/v1/createSession?key=${apiKey}`;
//   const response = await fetch(url, {
//     method: "POST",
//     headers: { "Content-Type": "application/json" },
//     body: JSON.stringify({
//       mapType: "satellite",
//       language: "en-US",
//       region: "US",
//     }),
//   });

//   if (!response.ok) {
//     throw new Error(`Failed to fetch session token: ${response.statusText}`);
//   }

//   const data = await response.json();
//   console.log(data);
//   return data.session;
// }

interface BoundingBox {
  center: THREE.Vector3;
  xAxis: THREE.Vector3;
  yAxis: THREE.Vector3;
  zAxis: THREE.Vector3;
}

export function boxContains(box: BoundingBox, point: THREE.Vector3): boolean {
  const boxTop = box.center.y + box.yAxis.y / 2;
  const boxBottom = box.center.y - box.yAxis.y / 2;
  const boxLeft = box.center.x - box.xAxis.x / 2;
  const boxRight = box.center.x + box.xAxis.x / 2;
  const boxFront = box.center.z + box.zAxis.z / 2;
  const boxBack = box.center.z - box.zAxis.z / 2;

  return (
    point.x >= boxLeft &&
    point.x <= boxRight &&
    point.y >= boxBottom &&
    point.y <= boxTop &&
    point.z >= boxBack &&
    point.z <= boxFront
  );
}

export function isPointInsideBoxWithRadius(point: THREE.Vector3, radius: number, box: BoundingBox): boolean {
  // Compute the half-extents (lengths of the axes)
  const halfWidth: number = box.xAxis.length();
  const halfHeight: number = box.yAxis.length();
  const halfDepth: number = box.zAxis.length();

  // Normalize the axes to get the orientation
  const xAxisNormalized: THREE.Vector3 = box.xAxis.clone().normalize();
  const yAxisNormalized: THREE.Vector3 = box.yAxis.clone().normalize();
  const zAxisNormalized: THREE.Vector3 = box.zAxis.clone().normalize();

  // Build the orientation matrix (world-to-local basis)
  const orientationMatrix: THREE.Matrix4 = new THREE.Matrix4();
  orientationMatrix.makeBasis(xAxisNormalized, yAxisNormalized, zAxisNormalized);

  // Compute the inverse orientation matrix (world-to-local)
  const inverseOrientationMatrix: THREE.Matrix4 = new THREE.Matrix4();
  inverseOrientationMatrix.copy(orientationMatrix).invert();

  // Step 1: Translate the point relative to the box center
  const pointRelativeToBox: THREE.Vector3 = point.clone().sub(box.center);

  // Step 2: Transform to local space
  const pointLocal: THREE.Vector3 = pointRelativeToBox.clone().applyMatrix4(inverseOrientationMatrix);

  // Step 3: Check if the point is within the local bounds with radius
  return (
    Math.abs(pointLocal.x) <= halfWidth + radius &&
    Math.abs(pointLocal.y) <= halfHeight + radius &&
    Math.abs(pointLocal.z) <= halfDepth + radius
  );
}

export interface Region {
  west: number;
  south: number;
  east: number;
  north: number;
  minHeight: number;
  maxHeight: number;
}

export interface Tile {
  boundingVolume: { box?: number[] };
  content?: { uri: string };
  children?: Tile[];
}

export function latLongToECEF(latitude: number, longitude: number, height: number): THREE.Vector3 {
  const a = 6378137.0; // Earth's semi-major axis (WGS84)
  const e2 = 0.00669438; // Eccentricity squared (WGS84)

  const latRad = (latitude * Math.PI) / 180;
  const lonRad = (longitude * Math.PI) / 180;

  const N = a / Math.sqrt(1 - e2 * Math.sin(latRad) * Math.sin(latRad));

  const x = (N + height) * Math.cos(latRad) * Math.cos(lonRad);
  const y = (N + height) * Math.cos(latRad) * Math.sin(lonRad);
  const z = ((1 - e2) * N + height) * Math.sin(latRad);

  return new THREE.Vector3(x, y, z);
}

export function offsetToGps(centerRefPoint: google.maps.LatLngAltitude, offset: Vec3): google.maps.LatLngAltitude {
  // Earth's radius in meters (approximation)
  const EARTH_RADIUS = 6378137;

  // Convert the reference point latitude and longitude to radians
  const latRad = centerRefPoint.lat * Math.PI / 180;
  // const lonRad = centerRefPoint.lng * Math.PI / 180;

  // Calculate meters per degree of latitude (constant)
  const metersPerLat = (Math.PI * EARTH_RADIUS) / 180;

  // Calculate meters per degree of longitude (varies with latitude)
  const metersPerLon = (Math.PI * EARTH_RADIUS * Math.cos(latRad)) / 180;

  // Calculate new latitude (x offset is north-south)
  const deltaLat = offset.x / metersPerLat;
  const newLat = centerRefPoint.lat + deltaLat;

  // Calculate new longitude (y offset is east-west)
  const deltaLon = -offset.y / metersPerLon;
  const newLon = centerRefPoint.lng + deltaLon;

  // Calculate new altitude (z offset is up-down)
  const newAlt = (centerRefPoint.altitude || 0) + offset.z;
  return new google.maps.LatLngAltitude(
    {
      lat: newLat,
      lng: newLon,
      altitude: newAlt
    });
}

export function gpsToOffset(centerRefPoint: google.maps.LatLngAltitude, gpsPoint: google.maps.LatLngAltitude): Vec3 {
  // Earth's radius in meters (approximation)
  const EARTH_RADIUS = 6378137; 
  // Convert the reference point latitude and longitude to radians
  const latRad = centerRefPoint.lat * Math.PI / 180;
  // const lonRad = centerRefPoint.lng * Math.PI / 180; 
  // Calculate meters per degree of latitude (constant)
  const metersPerLat = (Math.PI * EARTH_RADIUS) / 180;
  // Calculate meters per degree of longitude (varies with latitude)
  const metersPerLon = (Math.PI * EARTH_RADIUS * Math.cos(latRad)) / 180;
  // Calculate latitude offset
  const deltaLat = (gpsPoint.lat - centerRefPoint.lat) * metersPerLat;
  // Calculate longitude offset
  const deltaLon = (gpsPoint.lng - centerRefPoint.lng) * metersPerLon;
  // Calculate altitude offset
  const deltaAlt = (gpsPoint.altitude || 0) - (centerRefPoint.altitude || 0);
  // Return the offset as a Vec3
  return { x: deltaLat, y: -deltaLon, z: deltaAlt };
}

// Convert lat/long region to an approximate ECEF bounding box
export function latLongToECEFBoundingBox(
  latitude: number,
  longitude: number,
  radiusMeters: number,
  minHeight: number,
  maxHeight: number
): THREE.Box3 {
  const center = latLongToECEF(latitude, longitude, (minHeight + maxHeight) / 2);
  const delta = radiusMeters; // Approximate radius in meters

  // Create a simple axis-aligned bounding box in ECEF space
  const min = new THREE.Vector3(
    center.x - delta,
    center.y - delta,
    center.z - delta
  );
  const max = new THREE.Vector3(
    center.x + delta,
    center.y + delta,
    center.z + delta
  );

  return new THREE.Box3(min, max);
}

// Parse a Cesium `box` bounding volume into a BoundingBox
function parseCesiumBox(box: number[]): BoundingBox {
  return {
    center: new THREE.Vector3(box[0], box[1], box[2]),
    xAxis: new THREE.Vector3(box[3], box[4], box[5]),
    yAxis: new THREE.Vector3(box[6], box[7], box[8]),
    zAxis: new THREE.Vector3(box[9], box[10], box[11]),
  };
}

// Approximate intersection test between an ECEF Box3 and a Cesium `box`
function boxIntersectsBox(targetBox: THREE.Box3, cesiumBox: BoundingBox): boolean {
  // Convert Cesium `box` to an approximate AABB (axis-aligned bounding box) for simplicity
  const { center, xAxis, yAxis, zAxis } = cesiumBox;

  // Compute min/max corners of the Cesium box
  const corners: THREE.Vector3[] = [];
  const extents = [
    [-1, -1, -1],
    [-1, -1, 1],
    [-1, 1, -1],
    [-1, 1, 1],
    [1, -1, -1],
    [1, -1, 1],
    [1, 1, -1],
    [1, 1, 1],
  ];

  for (const extent of extents) {
    const corner = center
      .clone()
      .add(xAxis.clone().multiplyScalar(extent[0]))
      .add(yAxis.clone().multiplyScalar(extent[1]))
      .add(zAxis.clone().multiplyScalar(extent[2]));
    corners.push(corner);
  }

  const cesiumAABB = new THREE.Box3();
  cesiumAABB.setFromPoints(corners);

  // Test intersection with the target box
  return targetBox.intersectsBox(cesiumAABB);
}

export async function fetchTilesetJson(apiKey: string): Promise<any> {
  const url = `https://tile.googleapis.com/v1/3dtiles/root.json?key=${apiKey}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch tileset.json: ${response.statusText}`);
  }

  return response.json();
}

export function latLongToRegion(
  latitude: number,
  longitude: number,
  radiusMeters: number
): Region {
  const latRad = (latitude * Math.PI) / 180;
  const lonRad = (longitude * Math.PI) / 180;

  const earthRadius = 6378137;
  const latDelta = radiusMeters / earthRadius;
  const lonDelta = radiusMeters / (earthRadius * Math.cos(latRad));

  return {
    west: lonRad - lonDelta,
    south: latRad - latDelta,
    east: lonRad + lonDelta,
    north: latRad + latDelta,
    minHeight: -100,
    maxHeight: 1000,
  };
}

export function regionsIntersect(region1: Region, region2: Region): boolean {
  return (
    region1.east >= region2.west &&
    region1.west <= region2.east &&
    region1.north >= region2.south &&
    region1.south <= region2.north &&
    region1.maxHeight >= region2.minHeight &&
    region1.minHeight <= region2.maxHeight
  );
}

export interface DownloadableTile extends Tile {
  contentUrl: string;
}

export async function findTilesForRegion(
  tileset: any,
  // targetBox: THREE.Box3,
  point: THREE.Vector3,
  radius: number,
  baseUrl: string,
  apiKey: string,
): Promise<DownloadableTile[]> {
  const contentUris: DownloadableTile[] = [];
  let sessionString = '';
  async function traverse(tile: Tile) {
    if (!tile) return;
    // console.log({ tile });
    if (tile.boundingVolume && tile.boundingVolume.box) {
      const cesiumBox = parseCesiumBox(tile.boundingVolume.box);
      console.log({ point, radius, cesiumBox });
      if (isPointInsideBoxWithRadius(point, radius, cesiumBox)) {
        // console.log('Tile intersects region:', cesiumBox, targetBox, tile);
        if (tile.content && tile.content.uri && tile.content.uri.includes('.json') && !tile.children) {
          let contentUrl = new URL(tile.content.uri, baseUrl).toString();
          // console.log({ contentUrl });
          if (contentUrl.includes('.json')) {
            if (contentUrl.includes('?session=')) {
              // store session string for later use
              sessionString = contentUrl.split('?session=')[1].split('&')[0];
            }
            if (!contentUrl.includes('?session=')) {
              contentUrl += `?session=${sessionString}`;
            }
            if (!contentUrl.includes('&key=')) {
              contentUrl += `&key=${apiKey}`;
            }
            const newTilesetJsonRes = await fetch(contentUrl);
            if (newTilesetJsonRes.ok) {
              const newRoot = await newTilesetJsonRes.json();
              console.log({ newRoot: newRoot.root });
              for (const child of newRoot.root.children || [ newRoot.root ]) {
                await traverse(child);
              }
            } else {
              console.error(`Failed to fetch tileset.json: ${newTilesetJsonRes.statusText}`);
            }
          }

        } else if (tile.content && tile.content.uri && tile.content.uri.endsWith('.glb')) {
          let contentUrl = new URL(tile.content.uri, baseUrl).toString();
          console.log('Adding content uri:', contentUrl);
          const downloadableTile: DownloadableTile = { ...tile, contentUrl: contentUrl + `?session=${sessionString}&key=${apiKey}` };
          contentUris.push(downloadableTile);
        }
      } else {
        console.log('Tile does not intersect region');
        return;
      }
    }
    // console.log('tile.children:', tile.children, tile);
    if (tile.children) {
      for (const child of tile.children) {
        await traverse(child);
      }
    }
  }

  await traverse(tileset.root);
  return contentUris;
}

export function createRotationMatrixToAlignVectorWithZAxis(vector: THREE.Vector3): THREE.Matrix4 {
  // Check if the vector is zero
  if (vector.length() === 0) {
    throw new Error("Cannot align a zero vector");
  }

  // Normalize the input vector
  const vNorm: THREE.Vector3 = vector.clone().normalize();

  // Target direction (positive Z-axis)
  const zAxis: THREE.Vector3 = new THREE.Vector3(0, 0, 1);

  // Compute the rotation matrix
  const rotationMatrix: THREE.Matrix4 = new THREE.Matrix4();

  // Check if the vector is already aligned with zAxis or its negative
  const dot: number = vNorm.dot(zAxis);
  if (Math.abs(dot) > 0.99999) {
    if (dot < 0) {
      // 180-degree rotation around X-axis to flip direction
      rotationMatrix.makeRotationX(Math.PI);
    }
    // Else, identity matrix (no rotation needed)
  } else {
    // Compute the axis of rotation (cross product of vNorm and zAxis)
    const rotationAxis: THREE.Vector3 = new THREE.Vector3().crossVectors(vNorm, zAxis).normalize();

    // Compute the angle between vNorm and zAxis
    const angle: number = Math.acos(vNorm.dot(zAxis));

    // Build the rotation matrix
    rotationMatrix.makeRotationAxis(rotationAxis, angle);
  }

  return rotationMatrix;
}

export function computeZRotationToAlignLocalNorthWithGlobal(
  group: THREE.Group,
  localNorth: THREE.Vector3 = new THREE.Vector3(0, 1, 0),
  globalNorth: THREE.Vector3 = new THREE.Vector3(1, 0, 0)
): number {
  // Step 1: Transform localNorth to world space
  const worldMatrix: THREE.Matrix4 = new THREE.Matrix4();
  group.updateMatrixWorld();
  worldMatrix.copy(group.matrixWorld);
  const localNorthWorld: THREE.Vector3 = localNorth.clone().applyMatrix4(worldMatrix);

  // Step 2: Project both vectors onto the XY-plane
  const localNorthXY: THREE.Vector3 = new THREE.Vector3(localNorthWorld.x, localNorthWorld.y, 0);
  if (localNorthXY.length() < 0.00001) {
    localNorthXY.set(1, 0, 0); // Arbitrary fallback
  } else {
    localNorthXY.normalize();
  }

  const globalNorthXY: THREE.Vector3 = new THREE.Vector3(globalNorth.x, globalNorth.y, 0);
  if (globalNorthXY.length() < 0.00001) {
    globalNorthXY.set(1, 0, 0);
  } else {
    globalNorthXY.normalize();
  }

  // Step 3: Compute the angle between the projected vectors
  const dot: number = localNorthXY.dot(globalNorthXY);
  const angle: number = Math.acos(Math.min(Math.max(dot, -1), 1));

  // Step 4: Determine the direction of rotation using the cross product
  const cross: THREE.Vector3 = new THREE.Vector3().crossVectors(localNorthXY, globalNorthXY);
  let zRotation: number = angle;
  if (cross.z < 0) {
    zRotation = -zRotation;
  }

  return zRotation;
}

export function getSmallestTiles(tiles: DownloadableTile[]): DownloadableTile[] {
  const smallestTiles: DownloadableTile[] = [];
  for (const tile of tiles) {
    if (!tile.children) {
      smallestTiles.push(tile);
    }
  }
  return smallestTiles;
}

export async function loadTileContent(contentUrl: string): Promise<THREE.Group | null> {
  const loader = new GLTFLoader();

  // Create and configure DRACOLoader
  const dracoLoader = new DRACOLoader();
  dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
  loader.setDRACOLoader(dracoLoader);

  try {
    const gltf = await new Promise<THREE.Group>((resolve, reject) => {
      loader.load(
        contentUrl,
        (gltf) => resolve(gltf.scene),
        undefined,
        (error) => reject(error)
      );
    });
    return gltf;
  } catch (error) {
    console.error(`Failed to load tile content from ${contentUrl}:`, error);
    return null;
  } finally {
    // Clean up DRACOLoader
    dracoLoader.dispose();
  }
}

export async function generateTiles(site: Site, setProgress: React.Dispatch<React.SetStateAction<number>>, setLoading?: React.Dispatch<React.SetStateAction<boolean>>) {
  if (setLoading) setLoading(true);
  console.log('Generating tiles for site:', site);
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY!;
  const tilesetJson = await fetchTilesetJson(apiKey);
  console.log('tilesetJson:', tilesetJson);

  const latLng = toPoint(site.location);
  const point = latLongToECEF(latLng[1], latLng[0], site.elevation);

  console.log('point:', point);

  const tiles = await findTilesForRegion(tilesetJson, point, site.gridSize, 'https://tile.googleapis.com', apiKey);
  console.log('tiles:', tiles);

  const tilesWithoutChildren = tiles.filter(tile => !tile.children);
  console.log('tilesWithoutChildren:', tilesWithoutChildren.length);

  setProgress(1);
  const updatedSite = await saveRecord<Site>("sites", { ...site, numberOfTiles: tilesWithoutChildren.length });
  await chunkProcess(tilesWithoutChildren, 10, async (tile) => {
    const index = tilesWithoutChildren.indexOf(tile);
    await transferToBlob(tile.contentUrl, `techmage-tiles`, `${site._id}_${index}.glb`);
    if (index % 10 === 0) {
      console.log('Transferred', index, 'tiles');

    }
    setProgress(1 + (index / tilesWithoutChildren.length) * 99);
  });
  if (setLoading) setLoading(false);
  return updatedSite;
}

export async function deleteTiles(site: Site, setProgress: React.Dispatch<React.SetStateAction<number>>, setLoading?: React.Dispatch<React.SetStateAction<boolean>>) {
  if (setLoading) setLoading(true);
  console.log('Deleting tiles for site:', site);
  const blobsToDelete: string[] = getTileNames(site);
  setProgress(1);
  const updatedSite = await saveRecord<Site>("sites", { ...site, numberOfTiles: 0 });
  await chunkProcess(blobsToDelete, 10, async (blobName) => {
    await deleteBlob(
      "techmage-tiles",
      blobName
    );
    setProgress(1 + (blobsToDelete.indexOf(blobName) / blobsToDelete.length) * 99);
  });

  if (setLoading) setLoading(false);
  return updatedSite;
}

export function getTileNames(site: Site) {
  const id = site._id;
  const numberOfTiles = site.numberOfTiles || 0;
  const blobNames: string[] = [];
  for (let i = 0; i < numberOfTiles; i++) {
    blobNames.push(`${id}_${i}.glb`);
  }
  return blobNames;
}
